import { Category } from '@/entities/category';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { flow, pipe } from 'fp-ts/lib/function';
import { detailCodec, formCodec, listCodec } from './codec';
import {
  addVersionForUpdate,
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  decode,
  decodeId,
  decodeVersioned,
  fetchApi,
  setBody,
  setQuery,
  Versioned,
} from '../..';
import { CategoryQuery, encodeCategoryQuery } from '@/services/api/resources/category/query';
import { filterCodec } from '@/services/api/resources/filter';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';

const baseUrl = '/v2/categories';
const filterUrl = '/v2/filters/categories';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;

export const list = (query: CategoryQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeCategoryQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const filter = () => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const create = (category: Category) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(category))),
  chain(fetchApi),
  chain(decodeId),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(detailCodec)),
);

export const update = (id: number, category: Versioned<Category>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(category.version)),
  map(setBody(formCodec.encode(category.data))),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);
